import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import * as pb from '@/generated/console_pb'
import { setCustomerUnauthenticatedHandler } from '@/lib/auth/client'
import { whoAmI } from '@/lib/auth/customer-api'
import {
  clearCustomerSession,
  getCustomerSessionCsrf,
  hasStoredCustomerSession,
  setCustomerSessionCsrf,
} from '@/lib/auth/session'
import {
  changePassword as changePasswordHttp,
  login as passwordLoginHttp,
  logout as logoutHttp,
  selectAccount as selectAccountHttp,
  CustomerLoginError,
  type CustomerLoginPicker,
  type CustomerLoginSuccess,
} from '@/lib/auth/auth-http'

// The CUSTOMER portal's own auth context (ADR-179 P2 / D4). Deliberately a
// SEPARATE module from src/lib/auth/auth-context.tsx (the operator's) —
// its toPrincipal reads getOperator(); "instance principals never reach
// this browser app". This module's toPrincipal reads getCustomer() and
// NEVER getOperator() — that asymmetry is the entire security posture the
// separate-surface decision buys: there is no shared code path an operator
// session could accidentally flow through into customer views, or vice
// versa.

export type CustomerAuthStatus = 'checking' | 'unauthenticated' | 'authenticated'

export type CustomerRole = 'owner' | 'admin' | 'member'

export interface CustomerPrincipal {
  userId: string
  accountId: string
  email: string
  role: CustomerRole
}

// LoginOutcome mirrors auth-http.ts's CustomerLoginResult for the
// component layer: either the login completed (a session now exists — the
// context's principal/status are already updated) or the server returned
// an account picker (ADR-179 P4 — no session exists yet; the caller must
// render the picker and call selectAccount with the customer's choice).
export type LoginOutcome = { kind: 'session' } | { kind: 'picker'; picker: CustomerLoginPicker }

interface CustomerAuthContextValue {
  status: CustomerAuthStatus
  principal: CustomerPrincipal | null
  // loginWithPassword is the ONLY credential-entry path this context
  // exposes — the customer portal has no API-key mode. SSO (OIDC/SAML) is
  // a top-level browser REDIRECT (see auth-http.ts's ssoStartUrl), not a
  // context method — there is no JSON round-trip to wrap; the login page
  // navigates window.location directly and the callback lands the browser
  // back on this app with the cookie already set, at which point the
  // initial-probe effect below picks it up exactly like a page reload
  // after a password login.
  //
  // ADR-179 P4: account_ref is no longer a parameter — the server
  // auto-detects the candidate account(s) for email. The returned
  // LoginOutcome tells the caller whether a session was minted directly or
  // whether it must render the picker.
  loginWithPassword: (email: string, password: string) => Promise<LoginOutcome>
  // selectAccount redeems a picker_token for one of its pinned accounts
  // (ADR-179 P4's second step) and mints the session, updating
  // principal/status exactly like a direct loginWithPassword success.
  selectAccount: (pickerToken: string, accountId: string) => Promise<void>
  logout: () => Promise<void>
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>
}

const CustomerAuthContext = createContext<CustomerAuthContextValue | null>(null)

function toRole(r: pb.AccountRole): CustomerRole {
  switch (r) {
    case pb.AccountRole.ACCOUNT_ROLE_OWNER:
      return 'owner'
    case pb.AccountRole.ACCOUNT_ROLE_ADMIN:
      return 'admin'
    default:
      return 'member'
  }
}

function toCustomerPrincipal(res: pb.WhoAmIResponse): CustomerPrincipal {
  const cust = res.getCustomer()
  if (!cust) {
    // The portal only ever authenticates CustomerUsers (operators never
    // reach this surface — separate cookie name, separate interceptor
    // branch), so a response with no customer half populated means the
    // server considered this call something other than a customer session.
    throw new Error('WhoAmI returned no customer principal for a customer session')
  }
  return {
    userId: cust.getUserId(),
    accountId: cust.getAccountId(),
    email: cust.getEmail(),
    role: toRole(cust.getRole()),
  }
}

async function fetchCustomerPrincipal(): Promise<CustomerPrincipal> {
  const res = await whoAmI()
  return toCustomerPrincipal(res)
}

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<CustomerAuthStatus>('checking')
  const [principal, setPrincipal] = useState<CustomerPrincipal | null>(null)

  const goUnauthenticated = useCallback(() => {
    setStatus('unauthenticated')
    setPrincipal(null)
  }, [])

  useEffect(() => {
    setCustomerUnauthenticatedHandler(goUnauthenticated)
  }, [goUnauthenticated])

  useEffect(() => {
    if (!hasStoredCustomerSession()) {
      setStatus('unauthenticated')
      return
    }
    fetchCustomerPrincipal()
      .then((p) => {
        setPrincipal(p)
        setStatus('authenticated')
      })
      .catch(() => {
        // callConsoleCustomer's 401 handler already cleared the session
        // when the failure was Unauthenticated; for any other failure
        // (network) fail closed rather than granting access on doubt.
        clearCustomerSession()
        setStatus('unauthenticated')
      })
  }, [])

  // adoptSession is the shared "a session was just minted" tail shared by
  // loginWithPassword's single-account outcome and selectAccount's picker
  // redemption — both land on the SAME byte-for-byte session shape
  // (session_customer.go's issueSession is the one place either path mints
  // from), so both drive the SAME local-state update.
  const adoptSession = useCallback(async (res: CustomerLoginSuccess) => {
    setCustomerSessionCsrf(res.csrfToken)
    try {
      const p = await fetchCustomerPrincipal()
      setPrincipal(p)
      setStatus('authenticated')
    } catch (err) {
      clearCustomerSession()
      setStatus('unauthenticated')
      throw err
    }
  }, [])

  const loginWithPassword = useCallback(
    async (email: string, password: string): Promise<LoginOutcome> => {
      let result
      try {
        result = await passwordLoginHttp(email.trim(), password)
      } catch (err) {
        // Anti-enumeration: a 401 is the generic invalid-credentials case
        // (unknown email / wrong password / disabled user / zero-of-N
        // verified accounts all collapse to it server-side). Surface a
        // single generic message. For any OTHER status surface the
        // server's message.
        if (err instanceof CustomerLoginError && err.status === 401) {
          throw new Error('Invalid email or password.')
        }
        throw err instanceof Error ? err : new Error('Login failed. Try again.')
      }
      if (result.kind === 'picker') {
        // No session yet — nothing to adopt locally. The caller renders
        // the picker and eventually calls selectAccount.
        return { kind: 'picker', picker: result.picker }
      }
      await adoptSession(result.session)
      return { kind: 'session' }
    },
    [adoptSession],
  )

  const selectAccount = useCallback(
    async (pickerToken: string, accountId: string) => {
      let res
      try {
        res = await selectAccountHttp(pickerToken, accountId)
      } catch (err) {
        if (err instanceof CustomerLoginError && (err.status === 401 || err.status === 403)) {
          throw new Error('That account could not be selected. Please sign in again.')
        }
        throw err instanceof Error ? err : new Error('Account selection failed. Try again.')
      }
      await adoptSession(res)
    },
    [adoptSession],
  )

  const logout = useCallback(async () => {
    await logoutHttp()
    clearCustomerSession()
    goUnauthenticated()
  }, [goUnauthenticated])

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string) => {
      const csrf = getCustomerSessionCsrf()
      if (!csrf) {
        throw new Error('You must be signed in to change your password.')
      }
      await changePasswordHttp(oldPassword, newPassword, csrf)
      // The server revoked ALL sessions (including this one) and cleared
      // the cookies. Tear down locally and return to login so the customer
      // re-authenticates with the NEW password.
      clearCustomerSession()
      goUnauthenticated()
    },
    [goUnauthenticated],
  )

  const value = useMemo<CustomerAuthContextValue>(
    () => ({ status, principal, loginWithPassword, selectAccount, logout, changePassword }),
    [status, principal, loginWithPassword, selectAccount, logout, changePassword],
  )

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>
}

export function useCustomerAuth(): CustomerAuthContextValue {
  const ctx = useContext(CustomerAuthContext)
  if (!ctx) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider')
  }
  return ctx
}

// Exposed for tests that want to bypass the real network path.
export const __testing = { fetchCustomerPrincipal }
